<template>
  <div>
    <Card>
      <template #content>
        <div v-if="sponsors">
          <div class="d-flex align-items-center mb-3">
            <BasicButton
              label="追加"
              icon="pi pi-plus"
              class="me-2"
              :disabled="disabledAddMember"
              outlined
              @click="addMemberGroup"
            />
            <span
              style="font-size: 0.8rem"
              :class="{ 'text-secondary': disabledAddMember }"
            >
              一番下にフォームが追加されます
            </span>
          </div>

          <DataTable
            :value="form.members"
            :reorderable-columns="true"
            edit-mode="cell"
            class="mb-3"
            :table-style="isMobile ? 'width: 100%' : 'min-width: 50rem'"
            @row-reorder="(e) => onRowReorder(e)"
          >
            <Column
              row-reorder
              header-style="width: 3rem; min-width: 3rem"
              :reorderable-column="false"
            />
            <Column header-style="width: 80px; min-width: 40px">
              <template #body="{ index }">
                <BasicButton
                  v-if="form.members.length > 1"
                  icon="pi pi-trash"
                  variant="danger"
                  text
                  @click="removeInquiry(index)"
                />
              </template>
            </Column>
            <Column
              v-for="col of ['name', 'url']"
              :key="col"
              :field="col"
              :header="memberKeyToStr(col)"
              :style="{
                width: col === 'name' ? 'auto' : '400px',
                'min-width': '200px',
              }"
              :reorderable-column="false"
            >
              <template #body="{ field, index }">
                <FormItem
                  :value="form.members[index][field]"
                  form-type="text"
                  :form-id="field"
                  :errors="errors?.members?.[index]"
                  :placeholder="memberKeyToStr(field)"
                />
              </template>
              <template #editor="{ field, index }">
                <FormItem
                  v-model:value="form.members[index][field]"
                  form-type="text"
                  :form-id="field"
                  :placeholder="memberKeyToStr(field)"
                  :errors="errors?.members?.[index]"
                />
              </template>
            </Column>
            <Column
              field="image_blob"
              :header="
                memberKeyToStr('image_blob') +
                ` ※変更したい場合のみ画像を添付して下さい`
              "
              style="min-width: 800px"
              :reorderable-column="false"
            >
              <template #body="{ index }">
                <div
                  v-if="form.members[index]"
                  class="d-flex align-items-center"
                >
                  <!--
                  <ImageForm
                    :model-value="[form.members[index].imageBlob]"
                    :file-limit="1"
                    :current-count="form.members[index].imageBlob ? 1 : 0"
                    @update:model-value="
                      form.members[index].imageBlob = $event[0]
                    "
                  />
                  -->
                  <div
                    class="d-flex flex-column justify-content-center align-items-center me-3"
                  >
                    <Image
                      v-if="form.members[index].imageBlob"
                      :src="form.members[index].imageBlob"
                      width="200"
                      class="mb-2"
                      style="border-radius: 0.5rem"
                    />
                  </div>

                  <FormItemAttacheFile
                    v-if="form.members[index]"
                    :model-value="
                      form.members[index].imageBlob
                        ? [form.members[index].imageBlob]
                        : []
                    "
                    :file-limit="1"
                    :current-count="form.members[index].imageBlob ? 1 : 0"
                    :max-file-size-mb="5"
                    :accept="'image/*'"
                    form-id="image_blob"
                    class="mb-3"
                    :is-invalid="{}"
                    :errors="errors?.members?.[index] || {}"
                    @update:model-value="
                      form.members[index].imageBlob = $event[0]
                    "
                  />
                </div>
              </template>
            </Column>
            <Column header-style="width: 80px; min-width: 40px">
              <template #body="{ index }">
                <BasicButton
                  v-if="form.members.length > 1"
                  icon="pi pi-trash"
                  variant="danger"
                  text
                  @click="removeInquiry(index)"
                />
              </template>
            </Column>
          </DataTable>

          <BasicButton
            v-if="form.members && form.members.length > 0"
            label="追加"
            icon="pi pi-plus"
            outlined
            :disabled="disabledAddMember"
            @click="addMemberGroup"
          />

          <div class="d-flex justify-content-center my-4 py-4">
            <BasicButton
              icon="pi pi-times"
              label="キャンセル"
              variant="secondary"
              class="me-5"
              outlined
              @click="backto"
            />
            <BasicButton
              label="更新"
              button-type="submit"
              icon="pi pi-upload"
              @click="submit"
            />
          </div>
        </div>
        <i v-else class="pi pi-spin pi-spinner"></i>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { scrollToBottom, scrollToTop } from "/@/modules/ui";
import {
  useMqUtils,
  useZodScheme,
  useSponsors,
  useRouterUtil,
  useUser,
} from "/@/vue/composables";
import { BasicButton } from "/@/vue/components/Atom";
import { FormItem, ImageForm } from "/@/vue/components/Molecules";
import { FormItemAttacheFile } from "/@/vue/components/Molecules/Form";
import {
  SponsorForm as SponsorFormType,
  SponsorCheckScheme,
  SponsorClient,
} from "/@/types/sponsor.d";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Card from "primevue/card";
import Image from "primevue/image";

const { isMobile } = useMqUtils();

const { getUser } = useUser();
const { data: user, mutate: getUserMutate } = getUser();

const organizations = computed(() => {
  if (!user.value) return [];

  return user.value.organizations;
});

const { getSponsors, updateSponsors } = useSponsors();
const { data: sponsors, mutate: getSponsorsMutate } = getSponsors();

const { backto, goto } = useRouterUtil();

const { useFormAndErrors } = useZodScheme();
const { form, errors, startValidation } =
  useFormAndErrors<SponsorFormType>(SponsorCheckScheme);

function resetForm(sponsors?: SponsorClient) {
  form.id = sponsors?.id || undefined;
  form.members = sponsors?.members || [];
}

onMounted(async () => {
  if (!organizations.value) {
    await getUserMutate();
  }

  // スポンサーデータを最新の状態で取得
  await getSponsorsMutate();

  startValidation.value = true;
  if (sponsors.value) {
    resetForm(sponsors.value);
  }
  scrollToTop();
});

// スポンサーデータが変更されたときにフォームをリセット
watch(
  () => sponsors.value,
  (newSponsors) => {
    if (newSponsors) {
      resetForm(newSponsors);
    }
  },
  { deep: true }
);

function memberKeyToStr(key: string) {
  switch (key) {
    case "name":
      return "名前";
    case "url":
      return "URL";
    case "image_blob":
      return "画像";
  }
}

function onRowReorder(e: any) {
  const newMembers = e.value;

  form.members = newMembers;
}

function removeInquiry(nidx: number) {
  form.members.splice(nidx, 1);
}

const disabledAddMember = computed(() => {
  return !!errors.value.members;
});

function defaultMember() {
  return {
    name: "",
  };
}

function addMemberGroup() {
  if (disabledAddMember.value) return;

  form.members = [...form.members, defaultMember()];

  scrollToBottom();
}

async function submit() {
  if (!form.id) return;

  try {
    startValidation.value = true;

    // imageBlob がファイルオブジェクトの場合の処理
    const membersWithStringImageBlob = await Promise.all(
      form.members.map(async (member) => {
        let imageBlob = member.imageBlob;

        // ファイルオブジェクトの場合は文字列（データURL）として変換
        if (imageBlob && typeof imageBlob !== "string") {
          const fileBlob = imageBlob as any;
          if (fileBlob instanceof File) {
            imageBlob = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.readAsDataURL(fileBlob);
            });
          }
        }

        return {
          ...member,
          imageBlob: imageBlob as string | undefined,
        };
      })
    );

    const args = SponsorCheckScheme.parse({
      ...form,
      members: membersWithStringImageBlob,
    });

    if (await updateSponsors(form.id, args)) {
      alert("更新しました");
      // データを再取得してから画面遷移
      await getSponsorsMutate();
      goto({ name: "UsersSponsorsIndex" });
    } else {
      alert(
        "更新に失敗しました。添付されている画像が大きすぎます。画像が添付されていないにも関わらずこの表示が出る場合は、お手数ですが開発会社までご連絡をお願いします。"
      );
    }
  } catch (e) {
    alert("入力に問題があります。赤枠の項目を確認して下さい。");
    console.error(e);
  }
}
</script>

<style scoped></style>
